import { FacebookLoginProvider, GoogleLoginProvider, SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ReCaptcha2Component } from 'ngx-captcha';
import { EMPTY, defer, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchMapTo, take, tap } from 'rxjs/operators';

import { UserType } from 'src/app/account/login/UserType.enum';
import { LoginRequest } from 'src/app/account/login/login-request.model';
import { AgentDetailsResponse } from 'src/app/account/my-account/model/agentDetailsResponse.model';
import { RegisterRequest } from 'src/app/account/register/register-request.model';
import { RoutingService } from 'src/app/services/routing.service';
import { UserService } from 'src/app/services/shared/user.service';
import { TerraUtils } from 'src/app/shared/TerraUtils';
import { AppType } from 'src/app/shared/enums/AppType.enum';
import { CustomValidators } from 'src/app/shared/validators/custom.validators';
import { PasswordValidator } from 'src/app/shared/validators/password.validators';
import { UserPaymentType } from '../../../account/register/UserPaymentType.enum';
import { AppSettingsService } from '../../../services/app-settings.service';
import { TermType } from '../../../shared/enums/TermsTypes.enum';
import { LoggerService } from '../../../shared/errors/logger.service';

@Component({
  selector: 'terra-register-lp',
  templateUrl: './register-lp.component.html',
  styleUrls: ['./register-lp.component.scss']
})
export class RegisterLpComponent implements OnInit {
  showPassword = false;

  registerForm: UntypedFormGroup;
  private DUPLICATE_USER_NAME = 2;
  private CAPTCHA_INVALID = 3;
  private SSO_USER = 4;

  showSubmitError = false;
  error: string;
  isLoading = false;

  obtainingAccessToken = false;
  emailSessionToken: string = null;
  showCaptchaError = false;
  termType = TermType;

  ssoLoader$ = this.authService.initState;

  referrerLogo$ = this.userService.lp_referrerLogo$;
  @ViewChild('recaptcha') recaptcha: ReCaptcha2Component;

  constructor(
    private fb: UntypedFormBuilder,
    private userService: UserService,
    private router: Router,
    private authService: SocialAuthService,
    private route: ActivatedRoute,
    private titleService: Title,
    public routingService: RoutingService,
    public appSettingsService: AppSettingsService,
    private logger: LoggerService
  ) {
  }

  ngOnInit() {
    this.emailSessionToken = this.route.snapshot.queryParams.emailSessionToken;
    if (!this.emailSessionToken) {
      const returnUrl = this.routingService.getReturnUrlFromCookie;
      const urlParams = returnUrl && returnUrl.indexOf('?') > -1 ? new URLSearchParams(returnUrl.substring(returnUrl.indexOf('?'))) : null;
      this.emailSessionToken = urlParams && urlParams.get('emailSessionToken');
    }

    this.titleService.setTitle('Sign up' + TerraUtils.consts.LpPageTitleSuffix);

    this.registerForm = this.fb.group({
      isOrganization: [false],
      username: ['', [Validators.required, CustomValidators.EmailWithSpaces]],
      password: ['', [Validators.required, Validators.minLength(8), PasswordValidator.PasswordStrength]],
      recaptcha: ['', [CustomValidators.requiredIf(() => 
        !this.appSettingsService.isTestingEnv && !this.appSettingsService.isAutomationEnv)]]
    });

    const email = this.route.snapshot.queryParamMap.get('email');
    if (email) {
      this.registerForm.get('username').setValue(decodeURIComponent(email));
    }

    this.ssoSubscribe();
  }

  private handleUserLoggedInWithSso(socialUser: SocialUser) {
    const loginRequest = new LoginRequest(socialUser.email, null, socialUser.authToken ?? socialUser.idToken, socialUser.provider, UserType.Client);
    loginRequest.appType = AppType.LpApp;
    this.obtainLocalAccessToken(loginRequest);
  }

  ssoSubscribe() {
  this.authService.authState.pipe(
      filter(socialUser => !!socialUser),
      tap(socialUser => this.handleUserLoggedInWithSso(socialUser)),
      catchError(err => {
        if (err === 'User cancelled login or did not fully authorize.' || err?.error === 'popup_closed_by_user') {
          return EMPTY;
        }
        this.logger.error(`LP side -> signInWithExternalProvider`, err);
        return EMPTY;
      })).subscribe((user) => {
        console.log(user);
      });

  }

  public register() {
    this.showCaptchaError = false;
    if (this.registerForm.valid) {
      this.showSubmitError = false;

      const registerRequest = new RegisterRequest(
        null,
        null,
        this.registerForm.value.username,
        this.registerForm.value.password,
        this.registerForm.value.phone,
        null,
        UserPaymentType.Investor,
        this.registerForm.value.isOrganization,
        this.emailSessionToken
      );
      registerRequest.recaptchaToken = this.registerForm.value.recaptcha;
      registerRequest.appType = AppType.LpApp;

      this.isLoading = true;
      this.showPassword = false;

      this.userService.register(registerRequest).pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        catchError(err => {
          this.recaptcha?.resetCaptcha();

          const errResponse = err.error;
          if (errResponse === this.CAPTCHA_INVALID) {
            this.error = 'Bot verification failed';
          } else if (errResponse === this.DUPLICATE_USER_NAME) {
            this.error = 'This email address is already in use, please try a different address.';
          } else if (errResponse.message?.split(' ')?.length === 2 && errResponse.message?.split(' ')[0] === this.SSO_USER.toString()) {
            const provider = errResponse.message.split(' ')[1];
            this.error = `This email address is registered using ${provider} login - please use the buttons below to login.`;
          } else if (errResponse.modelState) {
            const errState = errResponse.modelState;
            if (errState['model.SignUp.Password']) {
              this.error = 'Your password should be at least 6 characters long, and contain digits and letters.';
            } else if (errState['model.SignUp.Email']) {
              this.error = 'The email address is invalid. Please check that you have entered it correctly.';
            }
          } else {
            this.error = 'An error has occurred, please contact support.';
          }
          this.showSubmitError = true;
          return throwError(err);
        })
      ).subscribe(response => {
        if (!response) {
          this.router.navigate([this.routingService.loginLpPage], {
            state: {
              usr: registerRequest.userName, pass: registerRequest.password
            }
          });
        } else {
          const agentDetails = response.clientDetails;
          if (!!agentDetails.email && !agentDetails.email.includes('@covercy.com')) {
            const _hsq = (window as any)._hsq = (window as any)._hsq || [];
            _hsq.push(['identify', {
              email: agentDetails.email
            }]);
            _hsq.push(['trackPageView']);
          }
          this.redirectAfterLogin(agentDetails);
        }
      },
        err => {
          const isSsoError = err.error.message?.indexOf(this.SSO_USER.toString()) === 0;
          if (err.error !== this.DUPLICATE_USER_NAME && err.error !== this.CAPTCHA_INVALID && !isSsoError) {
            this.logger.error('error in register LP component, ', err);
          }
        });
    } else {
      this.showCaptchaError = true;
      this.registerForm.updateValueAndValidity();
    }
  }

  public redirectAfterLogin(agentDetails: AgentDetailsResponse) {
    // if a return url exists, navigate to it
    if (agentDetails.mustVerifyEmail) { // gp & lp - email verification
      this.routingService.goToReturnUrl(TerraUtils.consts.paths.EMAIL_VERIFICATION);
      return;
    }
    // otherwise, navigate to ONBOARDING
    this.routingService.goToReturnUrl(this.routingService.lpOnboardingPage);
  }

  signInWithGoogle(event): void {
    if (this.obtainingAccessToken || this.isLoading) {
      return;
    }
    event.preventDefault();

    this.authService.initState.pipe(
      filter(isReady => !!isReady),
      take(1),
      switchMapTo(
        defer(() => this.authService.signIn(GoogleLoginProvider.PROVIDER_ID)).pipe(take(1))
      )
    )
      .subscribe(
        ssoUser => {
          if (ssoUser) {
            this.handleUserLoggedInWithSso(ssoUser);
          }
        },
        error => {
          if (error?.error === 'popup_closed_by_user') {
            return null;
          }
          this.logger.error(`LP side -> signInWithGoogle`, error);
          return null;
        });
  }

  signInWithFacebook(event): void {
    if (this.obtainingAccessToken || this.isLoading) {
      return;
    }
    event.preventDefault();

    this.authService.initState.pipe(
      filter(isReady => !!isReady),
      take(1),
      switchMapTo(
        defer(() => this.authService.signIn(FacebookLoginProvider.PROVIDER_ID)).pipe(take(1))
      )
    )
      .subscribe(
        ssoUser => {
          if (ssoUser) {
            this.handleUserLoggedInWithSso(ssoUser);
          }
        },
        error => {
          if (error === 'User cancelled login or did not fully authorize.') {
            return null;
          }
          this.logger.error(`LP side -> signInWithFacebook`, error);
          return null;
        });
  }

  obtainLocalAccessToken(loginModel: LoginRequest) {

    if (this.obtainingAccessToken || this.isLoading) {
      return;
    }
    this.obtainingAccessToken = true;
    this.isLoading = true;

    this.userService.obtainLocalAccessToken(loginModel)
      .pipe(
        map(response => {
          this.obtainingAccessToken = false;
          this.isLoading = false;
          return response;
        })
      )
      .subscribe(result => {
        const clientDetails = result.clientDetails;
        this.redirectAfterLogin(clientDetails);
      },
        error => {
          console.log('error', error);
        }
      );
  }
}
